import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { ConversionBlock, PriceTag } from '@/pages/guias/components/GuiaComponents';
import { ServiceBreadcrumb, SchemaService } from '../components/ServicioComponents';
import ReassuranceSection from '@/components/feature/ReassuranceSection';
import { SchemaPriceOffer } from '@/components/feature/SchemaOrg';

export default function MotorizacionToldosMalaga() {
  return (
    <>
      <title>Motorización de toldos en Málaga | Instalar motor y sensor de viento</title>
      <meta
        name="description"
        content="Motorización de toldos en Málaga. Convertimos tu toldo manual en eléctrico con mando a distancia. Instalación de motores, sensores de viento y sol. Presupuesto gratis."
      />
      <meta
        name="keywords"
        content="motorizar toldo malaga, instalar motor toldo malaga, sensor viento toldo malaga, toldo electrico malaga, motorizacion toldos malaga"
      />
      <link rel="canonical" href="https://toldos-malaga.es/motorizacion-toldos-malaga" />
      <SchemaService
        name="Motorización de toldos en Málaga"
        description="Servicio profesional de motorización de toldos en Málaga y provincia. Convertimos toldos manuales en eléctricos con mando a distancia. Instalación de motores tubulares, sensores de viento, sensores de sol y programación de finales de carrera."
        priceRange="desde 250 €"
      />
      <SchemaPriceOffer price={380} name="Motorización de toldo con sensor de viento en Málaga" description="Motorización completa con sensor de viento para protección contra el levante malagueño." />
      <meta property="og:title" content="Motorización de toldos en Málaga | Instalar motor y sensor de viento | Toldos Málaga" />
      <meta property="og:description" content="Motorización de toldos en Málaga. Convertimos tu toldo manual en eléctrico con mando a distancia. Instalación de motores, sensores de viento y sol. Presupuesto gratis." />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://toldos-malaga.es/motorizacion-toldos-malaga" />
      <meta property="og:image" content="https://toldos-malaga.es/og-image.jpg" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="Toldos Málaga" />
      <meta property="og:locale" content="es_ES" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Motorización de toldos en Málaga | Toldos Málaga" />
      <meta name="twitter:description" content="Convertimos tu toldo manual en eléctrico. Motores y sensores instalados con garantía. Presupuesto gratis." />
      <meta name="twitter:image" content="https://toldos-malaga.es/og-image.jpg" />
      <Navbar />
      <main>
        <ServiceBreadcrumb currentTitle="Motorización de toldos" />

        <section className="w-full max-w-[1280px] mx-auto px-4 md:px-6 pt-6 pb-10">
          <div className="relative rounded-2xl overflow-hidden h-[280px] md:h-[380px]">
            <img
              src="/images/024-servicio-motorizacion-hero.webp"
              alt="Toldo eléctrico motorizado con mando a distancia en una terraza de Málaga"
              title="Motorización de toldos en Málaga - Mando a distancia"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/15 to-transparent"></div>
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
              <span className="inline-block px-3 py-1 rounded-full bg-accent-500/90 text-foreground-900 text-xs font-semibold mb-3">
                Servicio de motorización
              </span>
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-white">
                Motorización de toldos en Málaga
              </h1>
            </div>
          </div>
        </section>

        <ReassuranceSection />

        <article className="w-full max-w-4xl mx-auto px-4 md:px-6 pb-14">
          <div className="space-y-6 text-foreground-700 leading-relaxed">

            <p className="text-lg text-foreground-600">
              Darle a la manivela cada mañana y cada noche está bien... los primeros meses. Pero cuando llevas años haciéndolo, y sobre todo en Málaga —donde el toldo se usa a diario durante 8 o 9 meses al año—, la manivela acaba cansando. Motorizar tu toldo no es un capricho: es comodidad diaria, seguridad frente al viento y, a la larga, menos averías por mal uso.
            </p>

            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10">
              ¿Qué incluye la motorización de un toldo?
            </h2>

            <p>
              Motorizar un toldo no es simplemente enchufar un motor. Es un proceso técnico que incluye varios pasos y componentes. Esto es lo que hacemos:
            </p>

            <div className="space-y-4">
              <div className="flex gap-4">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-primary-600 text-sm font-bold">1</span>
                </div>
                <div>
                  <strong className="text-foreground-900">Retirada del mecanismo manual.</strong>
                  <p className="text-sm text-foreground-600 mt-1">
                    Desmontamos la manivela, el reductor y el soporte antiguo. Dejamos el tubo de enrollado listo para recibir el motor tubular.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-primary-600 text-sm font-bold">2</span>
                </div>
                <div>
                  <strong className="text-foreground-900">Instalación del motor tubular.</strong>
                  <p className="text-sm text-foreground-600 mt-1">
                    Insertamos el motor dentro del eje de enrollado, fijamos los soportes y pasamos el cableado hasta la toma de corriente más cercana, con canalización protegida para exteriores.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-primary-600 text-sm font-bold">3</span>
                </div>
                <div>
                  <strong className="text-foreground-900">Programación de finales de carrera.</strong>
                  <p className="text-sm text-foreground-600 mt-1">
                    Configuramos el punto exacto de extensión máxima y de recogida total. Esto evita que el toldo fuerce contra la pared al abrirse o que se enrolle de más al cerrarse.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-primary-600 text-sm font-bold">4</span>
                </div>
                <div>
                  <strong className="text-foreground-900">Sincronización del mando.</strong>
                  <p className="text-sm text-foreground-600 mt-1">
                    Emparejamos el mando a distancia con el receptor del motor. Puede ser un mando individual, un mando múltiple para varios toldos, o incluso integrable con sistemas domóticos.
                  </p>
                </div>
              </div>
            </div>

            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10">
              Sensores: el plus que marca la diferencia en Málaga
            </h2>

            <p>
              Un toldo motorizado ya es un salto de comodidad, pero con sensores se convierte en un sistema inteligente que se cuida solo. En el clima de Málaga, estos accesorios no son un lujo: son una protección real para tu inversión.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="p-5 rounded-xl bg-secondary-100/70 border border-secondary-300/60">
                <div className="w-9 h-9 rounded-lg bg-accent-100 flex items-center justify-center mb-3">
                  <i className="ri-windy-line text-accent-600"></i>
                </div>
                <h3 className="font-heading font-bold text-foreground-900 text-base mb-1">Sensor de viento</h3>
                <p className="text-sm text-foreground-600">
                  Imprescindible en la costa malagueña. Detecta rachas fuertes de levante y recoge el toldo automáticamente antes de que el viento lo dañe. Cuando el viento amaina, puedes volver a extenderlo con normalidad.
                </p>
              </div>
              <div className="p-5 rounded-xl bg-secondary-100/70 border border-secondary-300/60">
                <div className="w-9 h-9 rounded-lg bg-accent-100 flex items-center justify-center mb-3">
                  <i className="ri-sun-line text-accent-600"></i>
                </div>
                <h3 className="font-heading font-bold text-foreground-900 text-base mb-1">Sensor de sol</h3>
                <p className="text-sm text-foreground-600">
                  Ideal para terrazas de hostelería o para quien sale de casa por la mañana. El toldo se extiende solo cuando detecta una intensidad de sol determinada, y se recoge al atardecer o cuando se nubla.
                </p>
              </div>
            </div>

            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10">
              ¿Merece la pena motorizar un toldo antiguo?
            </h2>

            <p>
              Esta es la pregunta que más nos hacen, y la respuesta sincera es: depende del estado del toldo. Si la estructura está en buen estado —brazos firmes, lona con tensión, sin corrosión seria en el tubo de enrollado—, motorizarlo es una inversión excelente. Ganas comodidad y alargas la vida útil del toldo porque reduces el desgaste por uso manual.
            </p>

            <p>
              Pero si el toldo tiene más de 15 años, los brazos están fatigados y la lona pide un cambio, motorizarlo sin hacer esas reparaciones previas es tirar el dinero. En ese caso, nuestra recomendación honesta es: cambia el toldo entero ya motorizado, o repara primero y motoriza después. No vamos a venderte una motorización si sabemos que en seis meses vas a tener que cambiar la lona o un brazo.
            </p>

            <div className="my-8">
              <ConversionBlock
                title="¿Cansado de la manivela? Motorizamos tu toldo"
                subtitle="Convertimos tu toldo manual en eléctrico en un solo día. Mando, motor y sensores instalados con garantía. Pide presupuesto sin compromiso."
              />
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mt-8">
              <PriceTag price="desde 250 €" description="Motorización estándar" />
              <PriceTag price="desde 380 €" description="Motor + sensor viento" />
              <p className="text-sm text-foreground-500">
                Precios orientativos con instalación incluida. Varía según potencia del motor y accesorios adicionales.
              </p>
            </div>

          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}